// @ts-strict-ignore
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ProjectOptionDefIds } from '@project/components/project/constants/project-option-def-ids';
import { ProjectStatesDef } from '@project/components/project/constants/project-states-def';
import { SaveProjectService } from '@project/components/project/project-save/save-project.service';
import { EditProjectService } from '@project/components/project/service/edit-project.service';
import { ProjectOptionContainer, ProjectOptionType, ProjectOptionWrapper, SelectionChoice } from '@project/components/project/util/project-option-data';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ProjectModel } from '@project/model/project.model';
import { ProjectFinalizationInfoVO, ProjectOptionDefVO, ProjectOptionLocalizationVO, ProjectOptionVO, ProjectSaveInfoVO, ProjectVO } from '@project/model/vo/project.vo';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { CustomValidators } from '@shared/custom-validators';
import { Log } from '@shared/debug/log';
import { InitManager } from '@shared/manager/init.manager';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { CloneUtils } from '@shared/util/clone-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { StringUtils } from '@shared/util/string-utils';
import { UserUtils } from '@user/service/user-utils';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { lastValueFrom, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Dictionary } from 'typescript-collections';
import { ProductionPlantDataService } from '@shared/service/production-plant-data.service';
import { UserModel } from '@user/model/user.model';
import { ArticleNumberLong } from '@project/model/article-number-long';
import { ProjectUserModel } from '@project/model/project-user.model';
import { ProjectDataService } from '@project/service/project-data.service';
import { ConfSettingsProjectService } from '../../../import-export/service/conf-settings-project.service';
import { ProjectComponentPresenter } from '../../../project-component.presenter';
import { ProjectOptionUtils } from '../../../project/util/project-option.utils';
import { IsProjectType } from '@project/model/is-project-type';
import { MdmGroupVO } from '@user/model/vo/user.vo';

@Component({
  standalone: false,
  selector: 'app-conf-settings',
  templateUrl: 'conf-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfSettingsComponent implements OnInit {

  @Input() projectOptionsEditable: boolean = true;
  @Input() projectNameEditable: boolean = true;
  @Input() allowEditOfOptionLabels: boolean = true;
  @Input() projectName: string;
  @Input() articleNumber: string;
  @Input() articleNumberLong: ArticleNumberLong;
  @Input() articleNumberExtension: string;
  @Input() drawingNumber: string;
  @Input() typeClassId: string;
  @Input() fNumber: string;
  @Input() version: number;
  @Input() documentVersion: number;
  @Input() tcVersion: number;
  @Input() useCustomArticleNumber: boolean;
  @Input() legacyFinalization: boolean;
  @Input() onlyDrawing: boolean;
  @Input() mdmGroup: MdmGroupVO;
  @Input() saveAndFinalizeProject: boolean;
  @Input() countryCodeString: string;
  @Input() confirmButtonLabel: string;

  @Output() public finish = new EventEmitter<boolean | undefined>();

  public form: FormGroup;
  public showRequiredFields: boolean = false;

  public currentProject: ProjectVO;

  public includeProjectFormData: boolean = false;
  public includeInternalOptions: boolean = false;
  public hasFinalizeProjectPermission: boolean = false;
  public hasRenameProjectPermission: boolean = false;
  public projectOptionContainer: ProjectOptionContainer<ProjectOptionWrapper> = new ProjectOptionContainer<ProjectOptionWrapper>();

  private _projectVersionDisplayStringWrappedProjectOption: ProjectOptionWrapper = null;
  private _projectSaveInfo: ProjectSaveInfoVO = null;
  private _restProjectOptions: ProjectOptionVO[] = [];
  private _waitForSaveEvent: boolean = false;

  private initialProjectOptions: ProjectOptionVO[];
  private definitionsOfProjectOptionsWereChanged: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private bsModalRef: BsModalRef,
    private cdRef: ChangeDetectorRef,
    private alertHelper: AlertHelper,
    private projectModel: ProjectModel,
    private projectDataService: ProjectDataService,
    private confSettingsProjectService: ConfSettingsProjectService,
    private editProjectService: EditProjectService,
    private projectOptionUtils: ProjectOptionUtils,
    private saveProjectService: SaveProjectService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private productionPlantDataService: ProductionPlantDataService,
    private projectUserModel: ProjectUserModel,
    private userModel: UserModel,
    private localeResourceManager: LocaleResourceManager,
  ) {
  }

  public ngOnInit(): void {
    this.definitionsOfProjectOptionsWereChanged = false;
    this.currentProject = this.projectModel.currentProject;
    this.includeProjectFormData = this.isProjectDataFormIncluded();
    this.includeInternalOptions = ProjectOptionUtils.canSeeInternalOptions(this.userModel.user);
    this.hasFinalizeProjectPermission = UserUtils.hasFinalizeProjectsPermission(this.userModel.user);
    this.hasRenameProjectPermission = UserUtils.hasPermissionToRenameProject(this.userModel.user, this.currentProject);
    this.projectNameEditable = this.projectNameEditable && this.hasRenameProjectPermission;
    if (!this.projectName) {
      this.projectName = this.currentProject.name;
    }

    this.mergeUserAndProjectOptions();

    this.projectDataService.loadProjectSaveInfo(this.currentProject.id).subscribe({
      next: (saveInfo: ProjectSaveInfoVO) => (this._projectSaveInfo = saveInfo),
      error: (error: any) => (this._projectSaveInfo = null)
    });

    this.cdRef.markForCheck();
  }

  public get articleNumberLongString() {
    return this.articleNumberLong && !this.articleNumberLong.isEmpty()
      ? this.articleNumberLong.reducedArticleNumberLong
      : this.currentProject?.articleNumberLong.reducedArticleNumberLong;
  }

  public get productionPlantChoices(): Observable<SelectionChoice[]> {
    return this.productionPlantDataService.getAllProductionPlants().pipe(map(
      productionPlants =>
        productionPlants.map(productionPlant => ({
          value: productionPlant.plantNo,
          label: `${productionPlant.plantNo} ${productionPlant.name}`
        }))
    ));
  }

  private isProjectDataFormIncluded(): boolean {
    const finalizationState: number = !this.currentProject ? ProjectStatesDef.FINALIZATION_STATE_NEW : this.currentProject.finalizationState;
    const documentVersion: number = !this.currentProject ? -1 : this.currentProject.documentVersion;
    const isArticleNumberGenerated: boolean = !this.currentProject ? false : this.currentProject.isArticleNumberGenerated;

    if (isArticleNumberGenerated) {
      return true;
    }
    return (
      finalizationState === ProjectStatesDef.FINALIZATION_STATE_PDM_EXPORT_DONE || (NumberUtils.isNumber(documentVersion) && documentVersion > -1)
    );
  }

  private mergeUserAndProjectOptions(): void {
    const optionContainer: ProjectOptionContainer<ProjectOptionWrapper> = new ProjectOptionContainer<ProjectOptionWrapper>();
    let unusedOptions: ProjectOptionVO[] = [];

    // save converted user options
    const projectOptions: ProjectOptionVO[] = this.copyProjectOptionArray(this.currentProject.options);
    const userOptions: ProjectOptionDefVO[] = this.projectUserModel.projectOptionDefs;
    ProjectOptionUtils.sortProjectOptionDefsByOrder(userOptions);

    for (const userOptionDef of userOptions) {
      // invisible items are now merged as well and visibility is handled later
      if (!userOptionDef.isVisible && !this.saveAndFinalizeProject) {
        continue;
      }

      // Is the userOption set in the project?
      const projectOption: ProjectOptionVO = ProjectOptionUtils.removeByIdentifier(projectOptions, userOptionDef.identifier);
      const convertedOption: ProjectOptionVO = this.createProjectOption(userOptionDef, projectOption);

      // see ticket 19003: For now we do not consider introducing missing project options as changes (missing options are only included with their labels, defaultValues are not applied)
      /* else if (this.currentProject.userId === this.projectModel.user.id) {
        // in case current logged in user is different form owner, we do not consider changes in project option definitions
        this.definitionsOfProjectOptionsWereChanged = true;
      }*/
      if (convertedOption.identifier === ProjectOptionDefIds.REMARK_ID && !userOptionDef.isMultiLine) {
        convertedOption.value = ProjectOptionUtils.getCleanedProjectOptionValue(convertedOption.value);
      }

      // created wrapper for project option to store "required" value by user option
      const wrappedProjectOption: ProjectOptionWrapper = this.projectOptionUtils.createWrapperByProjectOption(
        convertedOption,
        userOptionDef.isRequired,
        userOptionDef.isMultiLine,
        userOptionDef.maxLength
      );

      // edit production plant with selection box, if the current value is a valid choice
      if (convertedOption.identifier === ProjectOptionDefIds.PRODUCTION_PLANT) {
        wrappedProjectOption.optionType = ProjectOptionType.Selection;
        wrappedProjectOption.choices = this.productionPlantChoices;
      }

      if (ProjectOptionUtils.isForcedInvisible(wrappedProjectOption.identifier, this.projectModel.user, this.saveAndFinalizeProject)) {
        wrappedProjectOption.visible = false;
      }

      const optionAdded: boolean = optionContainer.addOption(wrappedProjectOption, this.includeInternalOptions);
      if (!optionAdded) {
        unusedOptions.push(wrappedProjectOption.projectOption);
      }

      if (wrappedProjectOption.projectOption.identifier === ProjectOptionDefIds.PROJECT_VERSION) {
        this._projectVersionDisplayStringWrappedProjectOption = wrappedProjectOption;
      }
    }

    this.form = this.formBuilder.group(this.initFormData(optionContainer));

    // Keep unused ProjectOptions for saving.
    unusedOptions = unusedOptions.concat(projectOptions);
    this._restProjectOptions = unusedOptions;

    const projectOptionsArray: ProjectOptionVO[] = optionContainer.getAll().map((element: ProjectOptionWrapper) => element.projectOption);
    this.initialProjectOptions = this.copyProjectOptionArray(projectOptionsArray);

    this.projectOptionContainer = optionContainer;
  }

  private createProjectOption(projectOptionDef: ProjectOptionDefVO, projectOption: ProjectOptionVO) {
    const convertedOption: ProjectOptionVO = ProjectOptionUtils.convertProjectOptionDefToProjectOption(projectOptionDef);

    if (projectOption) {
      convertedOption.id = projectOption.id;
      convertedOption.value = projectOption.value;
      convertedOption.localizations = this.getMergedProjectOptionLocalizations(convertedOption.localizations, projectOption.localizations);
    }

    return convertedOption;
  }

  private getMergedProjectOptionLocalizations(
    userOptionLocalizations: ProjectOptionLocalizationVO[],
    projectOptionLocalizations: ProjectOptionLocalizationVO[]
  ): ProjectOptionLocalizationVO[] {
    if (!projectOptionLocalizations || projectOptionLocalizations.length === 0) {
      return userOptionLocalizations;
    }
    if (!userOptionLocalizations || userOptionLocalizations.length === 0) {
      return projectOptionLocalizations;
    }

    const result: ProjectOptionLocalizationVO[] = projectOptionLocalizations.concat();
    const includedLocales: Dictionary<string, boolean> = new Dictionary<string, boolean>();
    for (const projectOptionLocalization of projectOptionLocalizations) {
      if (projectOptionLocalization) {
        includedLocales.setValue(projectOptionLocalization.locale, true);
      }
    }
    for (const userOptionLocalization of userOptionLocalizations) {
      if (userOptionLocalization && !includedLocales.containsKey(userOptionLocalization.locale)) {
        result.push(userOptionLocalization);
      }
    }
    return result;
  }

  private initFormData(optionsContainer: ProjectOptionContainer<ProjectOptionWrapper>): any {
    const formData: any = {};
    for (const optionWrapper of optionsContainer.getAll()) {
      // validation (even for required options) is not really needed for read-only mode and non-visible options
      // user is not able to edit such options anyway
      const includeValidator: boolean = this.projectOptionsEditable && optionWrapper.visible;
      if (includeValidator) {
        formData[optionWrapper.identifier] = [optionWrapper.value, optionWrapper.validator];
      } else {
        formData[optionWrapper.identifier] = [optionWrapper.value];
      }
    }
    this.addValidators(formData);
    return formData;
  }

  private addValidators(formData: [string, () => ValidatorFn]) {
    formData['projectName'] = this.projectNameEditable
      ? [
        this.projectName,
        Validators.compose([
          CustomValidators.projectNameValidator(false),
          CustomValidators.trimRequired,
          CustomValidators.maxLength(ProjectOptionUtils.MAX_PROJECT_NAME_LENGTH),
        ]),
      ]
      : [this.projectName];

    if(formData[ProjectOptionDefIds.CUSTOM_MATERIAL_NUMBER]) {
      const maxLengthCustomerMatNum = this.projectUserModel.getProjectOptionDef(ProjectOptionDefIds.CUSTOM_MATERIAL_NUMBER).maxLength;
      formData[ProjectOptionDefIds.CUSTOM_MATERIAL_NUMBER][1] = CustomValidators.maxLength(maxLengthCustomerMatNum);
    }
  }

  public checkControlValidity(controlName: string): boolean {
    const control = this.form.controls[controlName];
    return (control.invalid && control.dirty) || (control.invalid && this.showRequiredFields);
  }

  private copyProjectOptionArray(projectOptions: ProjectOptionVO[]): ProjectOptionVO[] {
    const newArray: ProjectOptionVO[] = [];
    if (projectOptions) {
      for (const projectOption of projectOptions) {
        newArray.push(CloneUtils.cloneProjectOptionVO(projectOption));
      }
    }
    return newArray;
  }

  public onEditOptionName(optionWrapper: ProjectOptionWrapper): void {
    if (!optionWrapper) {
      return;
    }
    const projectOption: ProjectOptionVO = optionWrapper.projectOption;
    if (!projectOption) {
      return;
    }

    this.projectComponentPresenter
      .openEditConfigurationOptionPopup(projectOption, this.localeResourceManager.currentLanguage.langKey)
      .then((option: ProjectOptionVO) => {
        if (option) {
          optionWrapper.displayName = this.projectOptionUtils.projectOptionLocalizationValueByLocaleChain(projectOption.localizations);
          this.cdRef.markForCheck();
        }
      });
  }

  private async onSave(): Promise<void> {
    if (!this.form.valid) {
      this.showRequiredFields = true;
      this.alertHelper.showError('changeProjectOptionPopup.error_valid.title', 'changeProjectOptionPopup.error_valid.message');
      return;
    }

    const formValues: any = this.form.value;

    this.projectName = formValues['projectName'];
    const projectOptions: ProjectOptionVO[] = [];
    for (const wrappedOption of this.projectOptionContainer.getAll()) {
      wrappedOption.value = formValues[wrappedOption.identifier];
      projectOptions.push(wrappedOption.projectOption);
    }

    if (!this.projectOptionsEditable) {
      await this.continueSave(projectOptions);
      return;
    }

    let projectVersionDisplayString: string = !this._projectVersionDisplayStringWrappedProjectOption
      ? ''
      : this._projectVersionDisplayStringWrappedProjectOption.projectOption.value;
    if (projectVersionDisplayString) {
      projectVersionDisplayString = StringUtils.trim(projectVersionDisplayString);
      if (this._projectVersionDisplayStringWrappedProjectOption) {
        this._projectVersionDisplayStringWrappedProjectOption.projectOption.value = projectVersionDisplayString;
      }
    }

    if (this._projectSaveInfo && this._projectSaveInfo.existsFinalizedProjectVersionDisplayString(projectVersionDisplayString)
      && this.currentProject.currentVersionDisplayString !== projectVersionDisplayString) {

      const answer = await this.alertHelper.showAlert(
        'projectRequestPopup.title.project_version_already_used',
        'projectRequestPopup.message.project_version_already_used',
        {0: projectVersionDisplayString},
        [AlertButton.YES, AlertButton.NO],
        "error",
        {
          yesLabel: 'projectRequestPopup.label.edit_project_version',
          noLabel: 'global.label.ignore'
        }
      );
      if (answer === AlertButton.YES) return;
    }
    await this.continueSave(projectOptions);
  }

  private async continueSave(projectOptions: ProjectOptionVO[]): Promise<void> {
    try {
      const projectRenamed: boolean = await lastValueFrom(this.renameProject());
      if (projectRenamed) {
        await this.saveOptions(projectOptions);
      }
    } catch (error) {
      Log.error('Could not rename project: ', error);
      this.onCancel();
    }
  }

  private renameProject(): Observable<boolean> {
    return this.projectNameEditable
      ? this.editProjectService.renameProject(this.currentProject, this.currentProject.isArticleProject, this.projectName)
      : of(true);
  }

  private async saveOptions(projectOptions: ProjectOptionVO[]): Promise<void> {
    this.currentProject.options = projectOptions.concat(this._restProjectOptions);
    this.currentProject.currentVersionDisplayString = ProjectUtils.getVersionDisplayString(this.currentProject, false);
    if (this.projectOptionsEditable && this.checkIfPropertiesChanged(projectOptions, this.initialProjectOptions)) {
      // TODO: dialog works only with the current project at the moment, this part will have to be adjusted for editing of non-current project
      this.projectModel.projectMetaDataChanged = true;
    }

    if (this.saveAndFinalizeProject) {
      this._waitForSaveEvent = true;
      const success = await this.saveProjectService.saveConfiguration(this.currentProject.id, this.currentProject, false, InitManager.PROJECT_OPTION_DATA_VERSION,
        'keepModificationDate', false, false);
      if (success) {
        this.saveProjectSuccess();
      }
    } else {
      this.onCancel(true);
    }
  }

  private saveProjectSuccess(): void {
    if (!this._waitForSaveEvent) {
      return;
    }
    this._waitForSaveEvent = false;
    if (!this.legacyFinalization) {
      const useSeriesForArticleNumber: boolean = IsProjectType.mcsGds(this.currentProject);
      this.projectDataService
        .finalizeProject(
          this.currentProject.id,
          this.currentProject.currentVersion,
          this.articleNumber,
          this.drawingNumber,
          this.articleNumberExtension,
          this.articleNumberLong,
          this.typeClassId,
          this.fNumber,
          this.currentProject.documentVersion,
          false,
          this.useCustomArticleNumber,
          this.countryCodeString,
          useSeriesForArticleNumber,
          this.tcVersion,
          this.onlyDrawing,
          this.mdmGroup,
        )
        .subscribe({
          next: (result: ProjectFinalizationInfoVO) => {
            this.confSettingsProjectService.finalizationSuccessHandler(result);
          },
          error: (error) => {
            if (error.faultMessageAlreadyDisplayed) {
              return;
            }
            this.alertHelper.showError('global.title.error', 'global.message.unexpected_error');
          }
        });
    } else {
      this.projectDataService
        .finalizeProject(
          this.currentProject.id,
          this.currentProject.currentVersion,
          this.articleNumber,
          this.drawingNumber,
          this.articleNumberExtension,
          this.articleNumberLong,
          this.typeClassId,
          this.fNumber,
          this.documentVersion,
          true,
          false,
          this.countryCodeString,
          false,
          this.tcVersion,
          this.onlyDrawing,
          this.mdmGroup,
        )
        .subscribe({
          next: (result: ProjectFinalizationInfoVO) => {
            this.confSettingsProjectService.finalizationSuccessHandler(result);
          },
          error: (error) => {
            if (error.faultMessageAlreadyDisplayed) {
              return;
            }
            this.alertHelper.showError('global.title.error', 'global.message.unexpected_error');
          }
        });
    }
    this.onCancel(true);
  }

  private findOptionByIdentifier(sourceData: ProjectOptionVO[], identifier: string): ProjectOptionVO {
    return sourceData.find((initialOption: ProjectOptionVO) => {
      return initialOption.identifier === identifier;
    });
  }

  private checkIfPropertiesChanged(targetData: ProjectOptionVO[], sourceData: ProjectOptionVO[]): boolean {
    if (this.definitionsOfProjectOptionsWereChanged) {
      return true;
    }
    const wereValuesChanged: boolean = targetData.some((property: ProjectOptionVO) => {
      const initialOption: ProjectOptionVO = this.findOptionByIdentifier(sourceData, property.identifier);
      return property.value !== initialOption.value;
    });

    if (wereValuesChanged) {
      return true;
    }

    const wereLabelsChanged: boolean = targetData.some((option: ProjectOptionVO) => {
      const initialOption: ProjectOptionVO = this.findOptionByIdentifier(sourceData, option.identifier);

      const initialLocalizedValue: string = this.projectOptionUtils.projectOptionLocalizationValueByLocaleChain(initialOption.localizations);
      const changedLocalizedValue: string = this.projectOptionUtils.projectOptionLocalizationValueByLocaleChain(option.localizations);
      return initialLocalizedValue !== changedLocalizedValue;
    });

    if (wereLabelsChanged) {
      return true;
    }

    return false;
  }

  public async onConfirm(): Promise<void> {
    await this.onSave();
  }

  public onCancel(success?: boolean): void {
    this.finish.emit(success);
    this.bsModalRef.hide();
  }

  public editArticleNumberMappingPopup(): void {
    this.projectComponentPresenter.openUserProjectOptionsPopup();
  }
}
